/**
 * Aplica branding de tenant (cliente_config.branding) en el DOM del LoginPortal.
 */
import logoEmpresaFallback from '../assets/logo_empresa.png';

const DEFAULT_COLORS = Object.freeze({
  primary: '#28466D',
  accent: '#CD9A62',
  primary_soft: '#E0EEFF',
  surface_soft: '#F3F8FF',
  theme_color: '#28466D',
});

export function getDefaultPortalLogo() {
  return logoEmpresaFallback;
}

/**
 * URLs de logo usables en el Gateway Tiger.
 * - Absolutas http(s) al DNS del tenant: OK.
 * - Assets del bundle (`/static/...`): OK.
 * - Relativas tipo `/api/backend/branding/...`: NO — en login-* resuelven al Gateway
 *   y Traefik/SPA devuelve HTML → ícono roto (post-logout / picker).
 */
export function isUsablePortalLogoUrl(url) {
  const value = String(url || '').trim();
  if (!value || value.startsWith('data:')) return Boolean(value);
  if (/^https?:\/\//i.test(value)) return true;
  if (value.startsWith('/static/')) return true;
  if (value.startsWith('/login-portal/static/')) return true;
  return false;
}

export function normalizePortalBranding(branding) {
  const raw = branding && typeof branding === 'object' ? branding : {};
  const logos = raw.logos && typeof raw.logos === 'object' ? raw.logos : {};
  const colors = raw.colors && typeof raw.colors === 'object' ? raw.colors : {};
  return {
    name: String(raw.name || '').trim(),
    logos: {
      empresa: String(logos.empresa || '').trim(),
      mark: String(logos.mark || '').trim(),
      mark_accent: String(logos.mark_accent || logos.markAccent || '').trim(),
      favicon: String(logos.favicon || '').trim(),
    },
    colors: {
      primary: String(colors.primary || DEFAULT_COLORS.primary).trim(),
      accent: String(colors.accent || DEFAULT_COLORS.accent).trim(),
      primary_soft: String(colors.primary_soft || colors.primarySoft || DEFAULT_COLORS.primary_soft).trim(),
      surface_soft: String(colors.surface_soft || colors.surfaceSoft || DEFAULT_COLORS.surface_soft).trim(),
      theme_color: String(
        colors.theme_color || colors.themeColor || colors.primary || DEFAULT_COLORS.theme_color,
      ).trim(),
    },
  };
}

export function resolvePortalLogoSrc(branding) {
  const normalized = normalizePortalBranding(branding);
  const candidates = [normalized.logos.empresa, normalized.logos.mark];
  for (const candidate of candidates) {
    if (isUsablePortalLogoUrl(candidate)) return candidate;
  }
  return logoEmpresaFallback;
}

export function applyPortalBrandTheme(branding) {
  if (typeof document === 'undefined') return;
  const { colors, logos, name } = normalizePortalBranding(branding);
  const root = document.documentElement;
  // Acentos de marca sin pisar superficies del dark mode (inline > :root[data-theme="dark"]).
  root.style.setProperty('--lp-blue-dark', colors.primary);
  root.style.setProperty('--lp-brand-accent', colors.accent);
  root.style.setProperty('--lp-brand-primary-soft', colors.primary_soft);
  root.style.setProperty('--lp-brand-surface-soft', colors.surface_soft);
  root.style.setProperty('--lp-spinner-ring', `color-mix(in srgb, ${colors.primary} 20%, transparent)`);
  root.style.removeProperty('--lp-background-btns');
  root.style.removeProperty('--lp-background-divs');

  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', colors.theme_color);

  const faviconCandidates = [logos.favicon, logos.mark, logos.empresa];
  const favicon = faviconCandidates.find((u) => isUsablePortalLogoUrl(u)) || '';
  if (favicon) {
    let link = document.getElementById('lp-brand-favicon');
    if (!link) {
      link = document.createElement('link');
      link.id = 'lp-brand-favicon';
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = favicon;
  }

  if (name) {
    document.title = `${name} - LoginPortal`;
  }
}

export { DEFAULT_COLORS as PORTAL_DEFAULT_COLORS };
